import React from 'react';
import { isFunction } from 'lodash';

/**
 * Custom hook for setting state like in class components
 */
export function useSetState<State>(initialState: State): [State, Function] {
  return React.useReducer(
    (state, newState) => ({ ...state, ...newState }),
    initialState,
  );
}

/**
 * Custom hook that safely tries to set state...it checks if component is mounted
 */
export function useSafeSetState(initialState) {
  const [state, setState] = useSetState(initialState);

  const mountedReference = React.useRef(false);

  React.useEffect(() => {
    mountedReference.current = true;
    return () => {
      mountedReference.current = false;
    };
  }, []);

  const safeSetState = (newState) => {
    if (mountedReference.current) {
      if (isFunction(newState)) {
        setState(newState({ ...state, previousState: state }));
      } else {
        setState({ ...newState, previousState: state });
      }
    }
  };

  return [state, safeSetState];
}

export function usePrevious<T = any>(value): T {
  const ref = React.useRef<any>();

  React.useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

export default {
  useSetState,
  useSafeSetState,
  usePrevious,
};
